/**
 * Codebase glue (DESIGN §8 NN-Newton core) — wires the REAL flowfield-NN + element geometry into the
 * generic, NN-free solvers (inverseDesign / optimize). Two pieces:
 *
 *   - surfaceCpFromNN — sample the NN's surface Cp along every element contour, split each contour into
 *     a suction (upper) / pressure (lower) strip, and return them as `SurfaceCpElement[]` (the shape
 *     `FlowMesh.surfaceCp` carries). This is the "make surfaceCp real, from the NN" of §B0/§8 step 1.
 *   - cpAtFactory — close over (nn, cond, baseDesign, specs, buildElements) to produce the
 *     `cpAt: vec → {upper,lower}` that solveInverse calls: unpack the packed vector onto the specs,
 *     write them onto the design, rebuild contours, run surfaceCpFromNN, return the targeted element.
 *
 * The NN forward is injected (default = nnInfer.forward) so both functions unit-test on a synthetic
 * Cp field WITHOUT trained weights (nnDesign.test.ts), mirroring nnAero.test's synthetic-Cp approach.
 * The raw9 row built here is byte-for-byte the one nnAero.nnSurfaceForces feeds the same NN, so the
 * Cp returned on the boundary is consistent between the force integral and these strips.
 *
 * Pure: no React/DOM, no module state. `SurfaceCpElement` is imported type-only (no THREE at runtime).
 */

import { type NN, forward as nnForward, signedDist } from '../flowfield-nn/nnInfer';
import type { NNCond, FoilElement } from '../flowfield-nn/nnAero';
import type { SurfaceCpElement } from '../foil/FlowVisLayer';
import { type ParamSpec, unpackFullVector } from './paramSpec';
import type { SurfaceCp } from './metrics';

/** The NN matmul forward, raw9 batch (flat N×9) → flat N×channels [Cp,u,v,Mach]. */
export type ForwardFn = (nn: NN, raw: Float32Array, N: number) => Float32Array;

export interface SurfaceCpOpts {
  /** Injected NN forward (default = nnInfer.forward); lets tests run without trained weights. */
  forward?: ForwardFn;
  /** Named elements whose signed distances feed raw9[4..6], in order (default ['main','vane','flap']). */
  distNames?: [string, string, string];
  /** Signed distance fed for an ABSENT named element. Default 10 = far field, matching nnAero /
   *  predictFlowMesh (the trained NN reads large +distance as "no such element here"; 0 would lie that
   *  the point sits on that element's surface). */
  absentDist?: number;
}

/**
 * Sample the NN's surface Cp along every element contour and split each into upper (suction) / lower
 * (pressure) strips, x/c-indexed. For each element we build, at each contour vertex, the raw9 row
 *   [alpha, mach, re/1e6, blow, d(main), d(vane), d(flap), x, z]
 * (d(·) = signed distance to that named element, `absentDist` when absent — exactly nnAero's row),
 * forward it through the NN, read the `Cp` channel, and bin the vertex onto the upper or lower strip by
 * which side of the element's LE→TE chord it lies (cross-product sign). x is normalised to x/c =
 * (x − x_LE)/chord per element. Each strip is sorted ascending in x/c. Pure; the NN forward is injected.
 */
export function surfaceCpFromNN(
  nn: NN,
  cond: NNCond,
  elements: FoilElement[],
  opts: SurfaceCpOpts = {},
): SurfaceCpElement[] {
  // Defensive: a malformed/half-loaded model must never crash the host workbench — degrade to "no Cp".
  if (!nn || !Array.isArray(nn.channels) || nn.channels.length === 0) return [];
  const fwd = opts.forward ?? nnForward;
  const names = opts.distNames ?? ['main', 'vane', 'flap'];
  const absent = opts.absentDist ?? 10;
  const iCp = nn.channels.indexOf('Cp');
  const NC = nn.channels.length;

  // Distance contours are looked up ONCE over the whole config (a vane's Cp depends on the main, etc.).
  const distContours = names.map((nm) => {
    const c = elements.find((e) => e.name === nm)?.contour;
    return c && c.length >= 3 ? c : null;
  });

  return elements.map((el) => {
    const c = el.contour;
    const N = c.length;
    if (N < 2) return { element: el.name, upper: [], lower: [] };

    const px = new Float32Array(N);
    const pz = new Float32Array(N);
    let leX = Infinity;
    let teX = -Infinity;
    for (let k = 0; k < N; k++) {
      const x = c[k]![0]!;
      px[k] = x;
      pz[k] = c[k]![1]!;
      if (x < leX) leX = x;
      if (x > teX) teX = x;
    }
    // A degenerate or non-finite contour (no proper LE→TE extent — e.g. a NaN control point reached the
    // geometry) has no surfaces to split; bail cleanly rather than emit a 1-point NaN strip downstream.
    if (!(teX > leX)) return { element: el.name, upper: [], lower: [] };
    const chord = teX - leX || 1;

    const dist = distContours.map((cc) => (cc ? signedDist(px, pz, cc) : null));
    const raw = new Float32Array(N * 9);
    for (let k = 0; k < N; k++) {
      const o = k * 9;
      raw[o] = cond.alpha;
      raw[o + 1] = cond.mach;
      raw[o + 2] = cond.re / 1e6;
      raw[o + 3] = cond.blow;
      raw[o + 4] = dist[0] ? dist[0][k]! : absent;
      raw[o + 5] = dist[1] ? dist[1][k]! : absent;
      raw[o + 6] = dist[2] ? dist[2][k]! : absent;
      raw[o + 7] = px[k]!;
      raw[o + 8] = pz[k]!;
    }
    const pred = fwd(nn, raw, N);

    // Split the closed contour into its two surface arcs at the LE (min-x) and TE (max-x) indices.
    // Robust for cambered/curved elements: a straight LE→TE chord mis-classifies points whose surface
    // crosses that chord (the curved vane does), interleaving the two surfaces into one strip — which
    // is exactly the Cp "sawtooth". Walking the contour arcs keeps each surface intact + monotone.
    let iLE = 0;
    let iTE = 0;
    for (let k = 0; k < N; k++) {
      if (px[k]! < px[iLE]!) iLE = k;
      if (px[k]! > px[iTE]!) iTE = k;
    }
    const walk = (from: number, to: number): { pts: [number, number][]; meanZ: number } => {
      const pts: [number, number][] = [];
      let zs = 0;
      for (let s = 0; s <= N; s++) {
        const k = (from + s) % N;
        pts.push([(px[k]! - leX) / chord, pred[k * NC + iCp]!]);
        zs += pz[k]!;
        if (k === to) break;
      }
      return { pts, meanZ: pts.length ? zs / pts.length : 0 };
    };
    const arcA = walk(iLE, iTE);
    const arcB = walk(iTE, iLE);
    // The higher-mean-z arc is the suction (upper) surface.
    const [upper, lower] = arcA.meanZ >= arcB.meanZ ? [arcA.pts, arcB.pts] : [arcB.pts, arcA.pts];
    upper.sort((a, b) => a[0] - b[0]);
    lower.sort((a, b) => a[0] - b[0]);
    return { element: el.name, upper, lower };
  });
}

export interface CpAtFactoryOpts<D> {
  /** Write the unlocked spec values back onto the design. Default: shallow `Record<string,number>`
   *  set by `spec.key` (good for flat designs + tests); production passes the schema-aware setter. */
  applyParams?: (design: D, specs: ParamSpec[]) => D;
  /** Which element's surfaces to return (default: the first element `buildElements` produces). */
  element?: string;
  /** Passed through to surfaceCpFromNN (inject the forward here in tests). */
  surfaceOpts?: SurfaceCpOpts;
}

/** Default param writeback: treat the design as a flat `Record<string,number>` keyed by `spec.key`. */
function defaultApplyParams<D>(design: D, specs: ParamSpec[]): D {
  const out: Record<string, number> = { ...(design as unknown as Record<string, number>) };
  for (const s of specs) out[s.key] = s.value;
  return out as unknown as D;
}

/**
 * Build the `cpAt: vec → {upper,lower}` that `solveInverse` (and a Cp-match `optimizeScalar`) calls.
 * For a full spec-order vector it: writes values onto the specs, writes them onto a fresh copy of
 * `baseDesign`, rebuilds the element contours via `buildElements`, runs
 * surfaceCpFromNN, and returns the targeted element's `{upper,lower}` strip. Pure given pure inputs;
 * `baseDesign`/`specs` are not mutated (the default writeback copies). NN-free under injection.
 */
export function cpAtFactory<D>(
  nn: NN,
  cond: NNCond,
  baseDesign: D,
  specs: ParamSpec[],
  buildElements: (design: D) => FoilElement[],
  opts: CpAtFactoryOpts<D> = {},
): (vec: number[]) => SurfaceCp {
  const apply = opts.applyParams ?? defaultApplyParams;
  return (vec: number[]): SurfaceCp => {
    const stepped = unpackFullVector(specs, vec);
    const design = apply(baseDesign, stepped);
    const elements = buildElements(design);
    const strips = surfaceCpFromNN(nn, cond, elements, opts.surfaceOpts);
    const want = opts.element
      ? strips.find((s) => s.element === opts.element)
      : strips[0];
    return want ? { upper: want.upper, lower: want.lower } : { upper: [], lower: [] };
  };
}
